import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

/**
 * Security Audit Tab — Feb 2026
 * Admin-only. Shows recent logins, failed attempts, settings/user changes,
 * remembered 2FA devices, and a big red "kick all sessions" button.
 */

const EVENT_LABEL = {
  login_success: '✅ Login',
  login_failed: '⛔ Failed login',
  settings_changed: '⚙️ Settings changed',
  email_config_changed: '📧 Email config changed',
  user_created: '👤+ User created',
  user_deleted: '👤− User deleted',
  user_role_changed: '🛡️ Role changed',
  user_password_changed: '🔑 Password changed',
  sessions_kicked_global: '🚪 All sessions kicked',
  trusted_device_revoked: '📱 Device revoked',
};

const SEVERITY_STYLE = {
  info: { bg: '#f0f9ff', border: '#bfdbfe', text: '#1e40af' },
  warning: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e' },
  critical: { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' },
};

const fmt = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('en-AU', {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: 'Australia/Sydney',
    });
  } catch {
    return iso;
  }
};

const shortAgent = (ua) => {
  if (!ua) return '';
  // Pull the OS + browser hint out of the user-agent string
  const m = ua.match(/\(([^)]+)\).*?(Chrome|Firefox|Safari|Edge|Opera)\/[\d.]+/i);
  if (m) return `${m[2]} — ${m[1].split(';')[0]}`;
  return ua.length > 60 ? ua.slice(0, 60) + '…' : ua;
};

export const SecurityAuditTab = ({ API }) => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);
  const [devices, setDevices] = useState([]);
  const [filterType, setFilterType] = useState('all');
  const [filterDays, setFilterDays] = useState(7);
  const [kickDialogOpen, setKickDialogOpen] = useState(false);
  const [kicking, setKicking] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [s, e, d] = await Promise.all([
        axios.get(`${API}/admin/audit/summary`),
        axios.get(
          `${API}/admin/audit/events?days=${filterDays}&limit=300${
            filterType !== 'all' ? `&event_type=${filterType}` : ''
          }`
        ),
        axios.get(`${API}/admin/audit/active-devices`),
      ]);
      setSummary(s.data);
      setEvents(e.data.events || []);
      setDevices(d.data.devices || []);
    } catch (err) {
      toast.error('Failed to load audit data: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [filterType, filterDays]);

  const handleRevokeDevice = async (deviceId, label) => {
    if (!window.confirm(`Revoke this trusted device (${label})? The user will need to complete 2FA on their next sign-in.`)) return;
    try {
      await axios.post(`${API}/admin/audit/revoke-device/${deviceId}`, {});
      toast.success('Device revoked');
      await loadAll();
    } catch (err) {
      toast.error('Failed to revoke: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleKickAll = async () => {
    setKicking(true);
    try {
      const res = await axios.post(`${API}/admin/audit/kick-all-sessions`, {});
      toast.success(`All sessions kicked. ${res.data.trusted_devices_revoked} devices revoked. You'll need to sign in again on your next click.`);
      setKickDialogOpen(false);
      await loadAll();
    } catch (err) {
      toast.error('Failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setKicking(false);
    }
  };

  if (loading && !summary) {
    return <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>Loading security audit…</div>;
  }

  const tile = (label, value, tone = '#0f172a') => (
    <div style={{
      padding: '1rem',
      background: '#fff',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      minWidth: '140px',
    }}>
      <div style={{ fontSize: '0.75rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: '1.75rem', fontWeight: 700, color: tone, marginTop: '0.25rem' }}>{value}</div>
    </div>
  );

  return (
    <div data-testid="security-audit-panel" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>

      {/* Summary tiles */}
      <Card>
        <CardHeader>
          <CardTitle>Security overview — last 24 hours</CardTitle>
          <CardDescription>
            Audit trail retained for {summary?.retention_days || 90} days rolling. Alerts are emailed on failed-login bursts, off-hours admin logins, email-config changes, and session kicks.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {tile('Logins (24h)', summary?.['24h']?.login_success ?? 0, '#059669')}
            {tile('Failed logins (24h)', summary?.['24h']?.login_failed ?? 0, (summary?.['24h']?.login_failed || 0) > 0 ? '#dc2626' : '#0f172a')}
            {tile('Settings changes (7d)', summary?.['7d']?.settings_changed ?? 0)}
            {tile('Email config changes (30d)', summary?.['30d']?.email_config_changed ?? 0, (summary?.['30d']?.email_config_changed || 0) > 0 ? '#d97706' : '#0f172a')}
            {tile('Active trusted devices', summary?.active_trusted_devices ?? 0)}
          </div>
        </CardContent>
      </Card>

      {/* Kick all sessions */}
      <Card style={{ borderColor: '#fca5a5' }}>
        <CardHeader>
          <CardTitle style={{ color: '#991b1b' }}>🚨 Kick everyone out</CardTitle>
          <CardDescription>
            Instantly invalidates every login (yours included) and revokes every remembered 2FA device.
            Use this if you suspect account compromise or after rotating passwords/keys.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button
            variant="destructive"
            onClick={() => setKickDialogOpen(true)}
            data-testid="kick-all-sessions-btn"
          >
            Kick all sessions
          </Button>
        </CardContent>
      </Card>

      {/* Active trusted devices */}
      <Card>
        <CardHeader>
          <CardTitle>Remembered 2FA devices ({devices.length})</CardTitle>
          <CardDescription>Browsers that skip the 2FA prompt for 30 days after login. Revoke anything you don't recognise.</CardDescription>
        </CardHeader>
        <CardContent>
          {devices.length === 0 ? (
            <p style={{ color: '#64748b', margin: 0 }}>No remembered devices.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem' }}>User</th>
                    <th style={{ padding: '0.5rem' }}>Device</th>
                    <th style={{ padding: '0.5rem' }}>IP</th>
                    <th style={{ padding: '0.5rem' }}>Last used</th>
                    <th style={{ padding: '0.5rem' }}>Expires</th>
                    <th style={{ padding: '0.5rem', textAlign: 'right' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {devices.map((d) => (
                    <tr key={d.id} style={{ borderTop: '1px solid #e2e8f0' }} data-testid={`device-row-${d.id}`}>
                      <td style={{ padding: '0.5rem' }}>
                        <strong>{d.username || '(unknown)'}</strong>
                        {d.full_name && <div style={{ color: '#64748b', fontSize: '0.8em' }}>{d.full_name}</div>}
                      </td>
                      <td style={{ padding: '0.5rem' }}>
                        {d.label}
                        {d.user_agent && <div style={{ color: '#94a3b8', fontSize: '0.75em' }}>{shortAgent(d.user_agent)}</div>}
                      </td>
                      <td style={{ padding: '0.5rem', fontFamily: 'monospace' }}>{d.ip}</td>
                      <td style={{ padding: '0.5rem' }}>{fmt(d.last_used_at)}</td>
                      <td style={{ padding: '0.5rem' }}>{fmt(d.expires_at)}</td>
                      <td style={{ padding: '0.5rem', textAlign: 'right' }}>
                        <Button size="sm" variant="outline" onClick={() => handleRevokeDevice(d.id, d.label)} data-testid={`revoke-device-${d.id}`}>
                          Revoke
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Event log */}
      <Card>
        <CardHeader>
          <CardTitle>Audit event log</CardTitle>
          <CardDescription>Newest first. Use the filters to narrow down.</CardDescription>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1rem', alignItems: 'flex-end' }}>
            <div>
              <Label htmlFor="filter-event-type">Event type</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger id="filter-event-type" style={{ width: '220px' }} data-testid="audit-filter-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All events</SelectItem>
                  {Object.entries(EVENT_LABEL).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="filter-days">Time window</Label>
              <Select value={String(filterDays)} onValueChange={(v) => setFilterDays(Number(v))}>
                <SelectTrigger id="filter-days" style={{ width: '160px' }} data-testid="audit-filter-days">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">Last 24 hours</SelectItem>
                  <SelectItem value="7">Last 7 days</SelectItem>
                  <SelectItem value="30">Last 30 days</SelectItem>
                  <SelectItem value="90">Last 90 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={loadAll} data-testid="audit-refresh-btn">↻ Refresh</Button>
          </div>

          {events.length === 0 ? (
            <p style={{ color: '#64748b' }}>No events match those filters.</p>
          ) : (
            <div style={{ overflowX: 'auto', maxHeight: '600px', overflowY: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#f8fafc' }}>
                  <tr style={{ textAlign: 'left' }}>
                    <th style={{ padding: '0.5rem' }}>Time (Sydney)</th>
                    <th style={{ padding: '0.5rem' }}>Event</th>
                    <th style={{ padding: '0.5rem' }}>User</th>
                    <th style={{ padding: '0.5rem' }}>IP</th>
                    <th style={{ padding: '0.5rem' }}>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {events.map((e) => {
                    const s = SEVERITY_STYLE[e.severity] || SEVERITY_STYLE.info;
                    return (
                      <tr key={e.id} style={{ borderTop: '1px solid #e2e8f0' }} data-testid={`audit-event-${e.id}`}>
                        <td style={{ padding: '0.5rem', whiteSpace: 'nowrap' }}>{fmt(e.created_at)}</td>
                        <td style={{ padding: '0.5rem' }}>
                          <span style={{
                            padding: '0.15rem 0.5rem',
                            borderRadius: '4px',
                            background: s.bg,
                            border: `1px solid ${s.border}`,
                            color: s.text,
                            fontSize: '0.8em',
                          }}>
                            {EVENT_LABEL[e.event_type] || e.event_type}
                          </span>
                        </td>
                        <td style={{ padding: '0.5rem' }}>{e.username || '—'}</td>
                        <td style={{ padding: '0.5rem', fontFamily: 'monospace', fontSize: '0.8em' }}>{e.ip}</td>
                        <td style={{ padding: '0.5rem', color: '#475569', fontSize: '0.85em' }}>
                          {Object.keys(e.details || {}).length > 0 && (
                            <code style={{ background: '#f1f5f9', padding: '0.15rem 0.4rem', borderRadius: '4px' }}>
                              {Object.entries(e.details).map(([k, v]) => `${k}=${typeof v === 'object' ? JSON.stringify(v) : v}`).join(', ')}
                            </code>
                          )}
                          {e.user_agent && (
                            <div style={{ color: '#94a3b8', fontSize: '0.9em', marginTop: '0.2rem' }}>{shortAgent(e.user_agent)}</div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Kick-all confirmation dialog */}
      <Dialog open={kickDialogOpen} onOpenChange={setKickDialogOpen}>
        <DialogContent data-testid="kick-all-confirm-dialog">
          <DialogHeader>
            <DialogTitle>Kick all sessions?</DialogTitle>
          </DialogHeader>
          <div style={{ padding: '0.5rem 0', lineHeight: 1.5 }}>
            <p><strong>This will immediately sign out every user, including you.</strong></p>
            <p>All remembered 2FA devices will be revoked, so everyone will need to re-enter their TOTP code on the next sign-in.</p>
            <p style={{ color: '#991b1b' }}>Use only if you suspect account compromise.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setKickDialogOpen(false)} disabled={kicking}>Cancel</Button>
            <Button variant="destructive" onClick={handleKickAll} disabled={kicking} data-testid="kick-all-confirm-btn">
              {kicking ? 'Kicking…' : 'Yes, kick everyone'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
